/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import SearchBar from "../components/SearchBar";
import StoreBox, { StoreBoxProps } from "../components/StoreBox";
import UserListItem from "../components/UserListItem";
import LoadingSpinner from "../components/LoadingSpinner";
import PlaceSuggestBottomSheet from "../components/PlaceSuggestBottomSheet";
import { searchHistoryService } from "../services/searchHistoryService";
import { toastUtil } from "../utils/toast";
import { mapApi, SearchPlaceResponse, SearchRegionResponse } from "../api/map";
import { kakaoApi } from "../api/kakao";
import { userApi, SearchMember } from "../api/user";
import { addPathToHistory } from "../utils/navigation";
import TabFilter, { TabFilterOption } from "../components/TabFilter";
import { useLocationStore } from "../stores/locationStore";

const Search: React.FC = () => {
  const navigate = useNavigate();

  /*
   * '기록할 사진 → 직접 찾기'로 심어 둔 사진을 버리는 일은 **여기서 하지 않는다.**
   *
   * 예전엔 이 화면을 떠날 때 버렸는데, 가는 곳이 `/store-review` 인지만 봤다.
   * 검색 결과를 누르면 `handlePlaceClick` 이 `/main-map?placeId=` 로 보내므로
   * 지도로 넘어가는 순간 사진이 통째로 지워졌다. 가게를 고르는 길은 화면
   * 하나가 아니라 여러 칸짜리 여정이다. 판단은 App.tsx 의 StagedPhotoGuard 가
   * 주소를 보고 한다.
   */
  const [searchParams] = useSearchParams();
  // 현재 위치 — 장소 검색을 근접 보정(있을 때만 전달, 없으면 정확도순 폴백)
  const userLocation = useLocationStore((state) => state.location);
  const getUserLocation = useLocationStore((state) => state.getUserLocation);
  const [searchValue, setSearchValue] = useState("");
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [placeSearchResults, setPlaceSearchResults] = useState<StoreBoxProps[]>(
    [],
  );
  const [regionSearchResults, setRegionSearchResults] = useState<
    SearchRegionResponse[]
  >([]);
  const [memberSearchResults, setMemberSearchResults] = useState<
    SearchMember[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  /** 멤버 자동완성(라이브 검색) 진행 중 — 결과 없을 때 빈 문구 대신 로딩 표시용 */
  const [isMemberLiveLoading, setIsMemberLiveLoading] = useState(false);
  /** 장소 자동완성(라이브 제안) 진행 중 */
  const [isPlaceLiveLoading, setIsPlaceLiveLoading] = useState(false);
  const [isPlaceSuggestOpen, setIsPlaceSuggestOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>("place");
  const [hideTab, setHideTab] = useState(false);
  const [placeholder, setPlaceholder] = useState<string>("검색");
  /** 엔터·키보드 완료 등으로 검색을 실행한 뒤에만 결과 영역 표시 */
  const [showResultsView, setShowResultsView] = useState(false);

  const filterOptions: TabFilterOption[] = [
    { value: "place", label: "장소 검색" },
    { value: "member", label: "멤버 검색" },
  ];

  // 최근 검색어 로드
  const loadRecentSearches = async () => {
    const history = await searchHistoryService.getSearchHistory();
    setRecentSearches(history.map((item) => item.keyword));
  };

  // 검색 화면 진입 시 위치가 없으면 한 번 시도 (지도 안 거치고 바로 검색 들어온 경우 대비)
  useEffect(() => {
    if (!userLocation) {
      getUserLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlaceClick = (placeId: string) => {
    navigate(`/main-map?placeId=${placeId}`);
  };

  const handleMapPreviewPlaceClick = (place: SearchPlaceResponse) => {
    const params = new URLSearchParams();
    params.set("placeLat", String(place.lat));
    params.set("placeLng", String(place.lng));
    params.set("placeName", place.name);
    params.set("placeAddress", place.address);
    navigate(`/main-map?${params.toString()}`);
  };

  // 장소 검색/제안 결과 → StoreBox props (전체검색·자동완성 공용)
  // 등록 장소(id 있음)는 placeId로 이동.
  // 미등록(suggest 카카오 결과)은 탭 시 등록(registerKakaoPlace) 후 그 place로 이동 → 리뷰 가능.
  // 그 외(좌표만)는 맵 프리뷰로 이동.
  const buildPlaceStoreBox = (place: SearchPlaceResponse): StoreBoxProps => {
    const hasCoords =
      place.lat != null &&
      place.lng != null &&
      Number.isFinite(place.lat) &&
      Number.isFinite(place.lng);
    // 라이브 자동완성 결과에서 액션(+리뷰·북마크·카드클릭)할 때 현재 검색어를
    // 최근 검색어로 저장 — 엔터 제출 없이 결과를 눌러도 기록에 남게 한다.
    const rememberKeyword = () => {
      void searchHistoryService.addSearchHistory(searchValue);
    };
    return {
      id: place.id,
      name: place.name,
      address: place.address,
      hasReview: place.has_my_review,
      isBookmarked: place.is_bookmarked,
      // 미등록(카카오) 결과: +(리뷰)·북마크 버튼이 누르는 순간 등록하도록 원본 전달
      kakao: place.kakao,
      onResultAction: rememberKeyword,
      onCardClick:
        place.id || place.kakao || hasCoords
          ? async (pid) => {
              rememberKeyword();
              if (pid) {
                handlePlaceClick(pid);
                return;
              }
              // 미등록 카카오 결과: 선택 시 우리 DB에 등록 → 등록된 place로 이동(리뷰 가능)
              if (place.kakao) {
                const reg = await kakaoApi.registerKakaoPlace(place.kakao);
                if (reg?.place_id) {
                  handlePlaceClick(String(reg.place_id));
                  return;
                }
                toastUtil.error("장소 등록에 실패했습니다");
                return;
              }
              if (hasCoords) {
                handleMapPreviewPlaceClick(place);
              }
            }
          : undefined,
    };
  };

  /** 지하철역 같은 지역 검색 결과 클릭 시 — 마커 없이 지도만 그 좌표로 이동 */
  const handleRegionClick = (region: SearchRegionResponse) => {
    const params = new URLSearchParams();
    params.set("moveLat", String(region.lat));
    params.set("moveLng", String(region.lng));
    navigate(`/main-map?${params.toString()}`);
  };

  // 검색 실행 (제출 시에만 호출, 최근 검색어 저장과 API 검색을 함께 수행)
  const handleSearch = async (keyword: string, filter?: string) => {
    if (!keyword.trim()) {
      setPlaceSearchResults([]);
      setMemberSearchResults([]);
      setShowResultsView(false);
      return;
    }

    setShowResultsView(true);
    setIsSearching(true);

    // 검색 기록에 추가
    await searchHistoryService.addSearchHistory(keyword);
    loadRecentSearches();

    const currentFilter = filter || activeFilter;

    // 필터에 따라 다른 API 호출
    try {
      if (currentFilter === "place") {
        // 장소 검색 (엔터: 카카오 전체검색 + 자동등록 포함)
        const response = await mapApi.searchPlaces(keyword, userLocation);

        const results: StoreBoxProps[] =
          response.places.map(buildPlaceStoreBox);

        setPlaceSearchResults(results);
        setRegionSearchResults(response.regions || []);
        setMemberSearchResults([]);
      } else {
        // 멤버 검색
        const response = await userApi.searchMembers(keyword);

        setMemberSearchResults(response.users);
        setPlaceSearchResults([]);
        setRegionSearchResults([]);
      }
    } catch (error) {
      console.error("검색 실패:", error);
      toastUtil.error("검색 중 오류가 발생했습니다");
      setPlaceSearchResults([]);
      setRegionSearchResults([]);
      setMemberSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // 초기화
  useEffect(() => {
    // 경로 히스토리에 추가
    addPathToHistory();

    // 쿼리 파라미터에서 필터와 탭 숨김 옵션 읽기
    const filterParam = searchParams.get("filter");
    if (filterParam === "place" || filterParam === "member") {
      setActiveFilter(filterParam);
    }

    const hideTabParam = searchParams.get("hideTab");
    if (hideTabParam === "true") {
      setHideTab(true);
    }

    const placeholderParam = searchParams.get("placeholder");
    if (placeholderParam) {
      setPlaceholder(decodeURIComponent(placeholderParam));
    }

    // 최근 검색어 로드
    loadRecentSearches();
  }, []);

  // 자동완성: 타이핑 중 debounce 라이브 검색 (멤버 + 장소).
  // - 멤버(/user/search): DB LIKE만 → 무해, 1자부터.
  // - 장소(/map/suggest): 카카오 1회 + DB우선, 자동등록 없음 → 부작용 0, 2자부터.
  useEffect(() => {
    const keyword = searchValue.trim();

    if (activeFilter === "member") {
      if (!keyword) {
        setIsMemberLiveLoading(false);
        return;
      }
      setIsMemberLiveLoading(true);
      const controller = new AbortController();
      const timer = setTimeout(async () => {
        try {
          const response = await userApi.searchMembers(
            keyword,
            controller.signal,
          );
          if (controller.signal.aborted) return;
          setMemberSearchResults(response.users);
          setPlaceSearchResults([]);
          setRegionSearchResults([]);
        } catch (error) {
          if (controller.signal.aborted) return; // 다음 입력에 의한 취소는 무시
          console.error("멤버 검색 실패:", error);
        } finally {
          if (!controller.signal.aborted) setIsMemberLiveLoading(false);
        }
      }, 300);
      return () => {
        clearTimeout(timer);
        controller.abort();
      };
    }

    if (activeFilter === "place") {
      // 1자부터 제안 (빈 문자열만 호출 안 함) — 한글 1음절 검색 허용
      if (keyword.length < 1) {
        setIsPlaceLiveLoading(false);
        return;
      }
      setIsPlaceLiveLoading(true);
      const controller = new AbortController();
      const timer = setTimeout(async () => {
        try {
          const response = await mapApi.suggestPlaces(
            keyword,
            controller.signal,
            userLocation,
          );
          if (controller.signal.aborted) return;
          setPlaceSearchResults(response.places.map(buildPlaceStoreBox));
          setRegionSearchResults(response.regions || []);
          setMemberSearchResults([]);
        } catch (error) {
          if (controller.signal.aborted) return;
          console.error("장소 제안 실패:", error);
        } finally {
          if (!controller.signal.aborted) setIsPlaceLiveLoading(false);
        }
      }, 300);
      return () => {
        clearTimeout(timer);
        controller.abort();
      };
    }
  }, [searchValue, activeFilter]);

  // 검색어 입력 변경 (멤버·장소 모두 라이브 자동완성, useEffect가 채움)
  const handleSearchChange = (value: string) => {
    setSearchValue(value);

    const keyword = value.trim();
    if (!keyword) {
      setPlaceSearchResults([]);
      setRegionSearchResults([]);
      setMemberSearchResults([]);
      setIsSearching(false);
      setShowResultsView(false);
      return;
    }

    // 멤버·장소 모두 1자부터 라이브 제안 영역 표시 (빈 입력만 최근 검색어)
    if (activeFilter === "member" || keyword.length >= 1) {
      setShowResultsView(true);
    } else {
      setShowResultsView(false);
    }
  };

  // 검색어 제출 (엔터, 모바일 키보드 완료 등 form submit)
  const handleSearchSubmit = (value: string) => {
    handleSearch(value);
  };

  // 닫기 버튼 클릭
  const handleClose = () => {
    if (searchValue.trim()) {
      // 검색어가 있으면 검색어 제거
      setSearchValue("");
      setPlaceSearchResults([]);
      setMemberSearchResults([]);
      setShowResultsView(false);
      // 최근 검색어 다시 로드
      loadRecentSearches();
    } else {
      // 검색어가 없으면 뒤로가기
      navigate(-1);
    }
  };

  // 최근 검색어 클릭
  const handleRecentSearchClick = (keyword: string) => {
    setSearchValue(keyword);
    handleSearch(keyword);
  };

  // 최근 검색어 삭제
  const handleRemoveRecentSearch = async (keyword: string) => {
    await searchHistoryService.removeSearchHistory(keyword);
    loadRecentSearches();
  };

  // 최근 검색어 모두 삭제
  const handleClearAllRecentSearches = async () => {
    await searchHistoryService.clearSearchHistory();
    loadRecentSearches();
  };

  const renderRecentSearches = () => {
    // 최근 검색어가 없으면 안내 문구/헤더 없이 빈 화면
    if (recentSearches.length === 0) {
      return <div className="flex-1 overflow-y-auto scrollbar-hide" />;
    }

    return (
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="w-full flex flex-col px-20px">
          <div className="w-full flex items-center justify-between py-16px">
            <p className="font-pretendard font-bold text-16px leading-125% text-brown-80%-main text-left">
              최근 검색
            </p>
            <button
              type="button"
              onClick={handleClearAllRecentSearches}
              className="font-pretendard font-bold text-16px leading-24px text-918063-80% text-right"
            >
              모두 삭제
            </button>
          </div>

          <div className="mt-16px w-full flex flex-col gap-16px">
            {recentSearches.map((keyword, index) =>
              renderRecentSearchItem(index, keyword),
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderRecentSearchItem = (index: number, keyword: string) => {
    return (
      <div
        key={index}
        className="w-full flex items-center justify-between py-8px cursor-pointer gap-8px"
        onClick={() => handleRecentSearchClick(keyword)}
      >
        <div className="flex-1 font-pretendard font-regular text-16px leading-24px text-brown-80%-main text-left">
          {keyword}
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveRecentSearch(keyword);
          }}
          className="w-24px h-24px flex items-center justify-center"
        >
          <img
            src="/images/close-bold-icon.png"
            alt="삭제"
            className="w-full h-full object-contain"
          />
        </button>
      </div>
    );
  };

  const handleSuggestPlace = () => {
    setIsPlaceSuggestOpen(true);
  };

  const handleClosePlaceSuggest = () => {
    setIsPlaceSuggestOpen(false);
  };

  const handleFilterChange = (value: string) => {
    setActiveFilter(value);
    const keyword = searchValue.trim();
    if (!keyword) return;

    // 전환 시 라이브 검색(useEffect)이 activeFilter 변경으로 재실행됨.
    // 멤버·장소 모두 1자부터 결과 영역 표시.
    if (value === "member" || keyword.length >= 1) {
      setShowResultsView(true);
    } else {
      setShowResultsView(false);
    }
  };

  const handleMemberClick = (userId: string) => {
    // 장소 결과 탭과 동일하게, 멤버 결과 탭 시에도 현재 검색어를 최근 검색어로 저장
    // (엔터 없이 자동완성 결과만 눌러도 기록에 남게)
    if (searchValue.trim()) {
      void searchHistoryService.addSearchHistory(searchValue);
    }
    navigate(`/user/${userId}/profile`);
  };

  const renderMemberSearchResults = () => {
    if (memberSearchResults.length > 0) {
      return (
        <div className="w-full flex flex-col px-20px py-16px gap-16px">
          {memberSearchResults.map((member) => (
            <div
              key={member.id}
              onClick={() => handleMemberClick(member.id)}
              className="cursor-pointer"
            >
              <UserListItem
                id={member.id}
                nickname={member.nickname}
                username={member.username}
                profileImage={member.profile_image ?? undefined}
              />
            </div>
          ))}
        </div>
      );
    }

    // 라이브 검색 중이고 아직 결과가 없으면 빈 문구 대신 로딩 표시 (입력 중 깜빡임 방지)
    if (isMemberLiveLoading) {
      return (
        <div className="w-full flex items-center justify-center py-40px">
          <LoadingSpinner size={28} borderWidth={4} />
        </div>
      );
    }

    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center px-16px pointer-events-none empty-state-center">
        <p className="font-pretendard font-regular text-16px leading-180% text-[#6B5E46] text-center">
          검색 결과가 없습니다
        </p>
      </div>
    );
  };

  const renderPlaceSearchResults = () => {
    const hasRegions = regionSearchResults.length > 0;
    const hasPlaces = placeSearchResults.length > 0;

    if (hasRegions || hasPlaces) {
      return (
        <div className="w-full flex flex-col px-20px pt-30px pb-16px">
          {hasRegions && (
            <>
              <div className="flex flex-col gap-36px">
                {regionSearchResults.map((region) => (
                  <div
                    key={region.kakao_id}
                    className="flex items-center gap-12px cursor-pointer"
                    onClick={() => handleRegionClick(region)}
                  >
                    <img
                      src="/images/region-icon.png"
                      alt="지역"
                      className="w-24px h-24px flex-shrink-0"
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                      <p className="font-pretendard font-bold text-16px text-brown-80%-main truncate">
                        {region.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {hasPlaces && (
                <div
                  className="mt-24px mb-24px h-[1px] w-full bg-brown-80%-main opacity-20"
                  aria-hidden
                />
              )}
            </>
          )}
          {hasPlaces && (
            <div className="flex flex-col gap-36px">
              {placeSearchResults.map((store, idx) => (
                <StoreBox
                  key={store.id ?? `unsaved-${idx}`}
                  {...store}
                  showRemoveButton={false}
                />
              ))}
            </div>
          )}

          {/* 결과 하단 장소 제안 유도 */}
          <p
            className="mt-36px font-pretendard font-regular text-16px leading-180% text-[#6B5E46] text-center cursor-pointer"
            onClick={handleSuggestPlace}
          >
            찾고 있는 장소가 없나요?
          </p>
        </div>
      );
    }

    // 라이브 제안 중이고 아직 결과가 없으면 빈 문구 대신 로딩 (입력 중 깜빡임 방지)
    if (isPlaceLiveLoading) {
      return (
        <div className="w-full flex items-center justify-center py-40px">
          <LoadingSpinner size={28} borderWidth={4} />
        </div>
      );
    }

    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center px-16px pointer-events-none empty-state-center">
        <p
          className="pointer-events-auto font-pretendard font-regular text-16px leading-180% text-[#6B5E46] text-center cursor-pointer"
          onClick={handleSuggestPlace}
        >
          찾고 있는 장소가 없나요?
        </p>
      </div>
    );
  };

  const renderSearchResults = () => {
    return (
      <>
        {/* 탭 (hideTab이 false일 때만 표시) */}
        {!hideTab && (
          <TabFilter
            options={filterOptions}
            activeValue={activeFilter}
            onChange={handleFilterChange}
          />
        )}

        <div className="flex-1 overflow-y-auto scrollbar-hide relative">
          {isSearching ? (
            <>
              <div className="w-full flex items-center justify-center py-40px">
                <LoadingSpinner size={28} borderWidth={4} />
              </div>
            </>
          ) : (
            <>
              {activeFilter === "place" ? (
                <>
                  {/* 장소 검색 결과 */}
                  {renderPlaceSearchResults()}
                </>
              ) : (
                <>
                  {/* 멤버 검색 결과 */}
                  {renderMemberSearchResults()}
                </>
              )}
            </>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden relative">
        {/* 검색 바 영역 */}
        <div className="w-full py-12px px-20px">
          <div className="w-full flex items-center gap-16px">
            <div className="flex-1">
              <SearchBar
                placeholder={placeholder}
                value={searchValue}
                onSearch={handleSearchChange}
                onSubmit={handleSearchSubmit}
                enterKeyHint="done"
                height={44}
              />
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="
                font-pretendard font-bold text-16px
                text-brown-80%-main
                text-center
              "
            >
              닫기
            </button>
          </div>
        </div>

        {/* 검색 결과 또는 최근 검색어 영역 */}
        {showResultsView || isSearching ? (
          <>
            {/* 검색어가 있거나 검색 중이면 검색 결과 표시 */}
            {renderSearchResults()}
          </>
        ) : (
          <>
            {/* 검색어가 없고 검색 중이 아니면 최근 검색어 표시 */}
            {renderRecentSearches()}
          </>
        )}
      </div>

      {/* 장소 제안 바텀 시트 */}
      <PlaceSuggestBottomSheet
        isOpen={isPlaceSuggestOpen}
        onClose={handleClosePlaceSuggest}
        initialStoreName={searchValue.trim()}
      />
    </div>
  );
};

export default Search;
